import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router';
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBackIos,
  Edit,
  Delete,
  Check,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import Page from '../../components/Layout/Page';
import useStyles from './styles';
import CustomTable from '../../components/General/CustomTable';
import { getDateOnlyFormat } from '../../utils/helpers';
import AddProgramModal from './Modals/AddProgramModal';
import {
  postContract, getEnrollmentSummaryByStudentId, getContractById, putStudyPlan,
} from '../../requests/api/students';
import { getSubjects } from '../../requests/api/subjects';
import handleApiResponse from '../../utils/handleApiResponse';
import { PostContractData } from '../Student/StudentListView/types';
import { DRAFT } from '../../utils/contract';

interface Test {
  id: number;
  name: string;
}

interface StudentRelevantInfo {
  testsToTake: Test[];
  testsComment: string;
  locationName: string;
  inscriptionYear: number;
  classYear: string;
  schoolName: string;
}

const formatDateRange = (
  startDate: string | Date | undefined,
  endDate: string | Date | undefined,
) => {
  if (startDate && endDate) {
    const formattedStart = getDateOnlyFormat(startDate, '/', true);
    const formattedEnd = getDateOnlyFormat(endDate, '/', true);
    return `${formattedStart} - ${formattedEnd}`;
  }
  return '-';
};

const STUDY_PLAN_TABLE_HEADERS = [
  {
    label: 'Asignatura',
    key: 'subjectName',
    valueType: 'string',
  },
  {
    label: 'Programa',
    key: 'programName',
    valueType: 'string',
  },
  {
    label: 'Rango de fechas',
    key: 'dateRange',
    valueType: 'custom',
    functionToApply: (dateRange: { startDate?: string | Date; endDate?: string | Date }) => {
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        return formatDateRange(dateRange.startDate, dateRange.endDate);
      }
      return '-';
    },
  },
  {
    label: 'Arancel Original',
    key: 'originalFee',
    valueType: 'ccy',
  },
  {
    label: 'Descuento',
    key: 'discount',
    valueType: 'ccy',
  },
  {
    label: 'Comentario',
    key: 'comment',
    valueType: 'string',
  },
  {
    label: 'Arancel',
    key: 'finalFee',
    valueType: 'ccy',
  },
  {
    label: 'Acciones',
    key: 'actions',
    valueType: 'other',
  },
];

type ProgramTableData = {
  id?: string;
  subjectId?: string;
  subjectName: string;
  year: number;
  level: string;
  classYear: string;
  programId?: string;
  programName: string;
  dateRange: { startDate: string | Date; endDate: string | Date };
  originalFee: number;
  discount: number;
  comment: string;
  finalFee: number;
  actions?: React.ReactNode;
};

const agreementOptions = [
  { id: 'Embajador', name: 'Embajador' },
  { id: 'Ex-alumno', name: 'Ex-alumno' },
  { id: 'Colegio convenio', name: 'Colegio convenio' },
  { id: 'Segundo hermano', name: 'Segundo hermano' },
  { id: 'Otro', name: 'Otro' },
];

function CreateStudyPlan() {
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const params = new URLSearchParams(location.search);
  const studentId = params.get('id') || '';
  const contractId = params.get('contractId') || '';
  const isEditMode = !!contractId;
  const [studentRelevantInfo, setStudentRelevantInfo] = useState<StudentRelevantInfo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [programsData, setProgramsData] = useState<ProgramTableData[]>([]);
  const [editingProgram, setEditingProgram] = useState<ProgramTableData | null>(null);
  const [matriculaValor, setMatriculaValor] = useState<string>('0');
  const [matriculaDescuento, setMatriculaDescuento] = useState<string>('0');
  const [matriculaConvenio, setMatriculaConvenio] = useState<string>('');
  const [matriculaComentario, setMatriculaComentario] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [studentInscriptionYear, setStudentInscriptionYear] = useState<number | null>(null);
  const [subjects, setSubjects] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    // Selecting a convenio defaults the discount to 50%, but only when the user
    // hasn't already entered a custom value; a manually-entered value prevails.
    if (matriculaConvenio && matriculaConvenio !== '') {
      if (!matriculaDescuento || matriculaDescuento === '0') {
        setMatriculaDescuento('50');
      }
    }
  }, [matriculaConvenio]);

  const handleEditProgram = (programIndex: number) => {
    const programToEdit = programsData[programIndex];
    if (programToEdit) {
      setEditingProgram(programToEdit);
      setModalOpen(true);
    }
  };

  const handleDeleteProgram = (programIndex: number) => {
    setProgramsData((prev) => prev.filter((_, index) => index !== programIndex));
  };

  const validateStudyPlan = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (programsData.length === 0) {
      errors.push('Debe agregar al menos un programa');
    }

    const matriculaValorNum = Number(matriculaValor);
    if (matriculaValor === '' || Number.isNaN(matriculaValorNum) || matriculaValorNum < 0) {
      errors.push('Debe ingresar un valor de matrícula igual o mayor a 0');
    }

    const matriculaDescuentoNum = Number(matriculaDescuento);
    if (matriculaDescuentoNum > 100) {
      errors.push('El porcentaje de descuento no puede ser mayor a 100%');
    }

    programsData.forEach((program, index) => {
      const programLabel = program.programName || program.subjectName || `#${index + 1}`;

      if (program.originalFee < 0) {
        errors.push(`El arancel del programa "${programLabel}" no puede ser negativo`);
      }

      if (program.discount < 0) {
        errors.push(`El descuento del programa "${programLabel}" no puede ser negativo`);
      }

      if (program.discount > program.originalFee) {
        errors.push(`El descuento del programa "${programLabel}" no puede ser mayor al arancel`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const handleCreateStudyPlan = async () => {
    const validation = validateStudyPlan();

    if (!validation.isValid) {
      if (validation.errors.length > 0) {
        enqueueSnackbar(validation.errors[0], {
          variant: 'error',
          autoHideDuration: 6000,
        });
      }
      return;
    }

    if (!studentId) {
      enqueueSnackbar('Error: No se encontró el ID del estudiante', {
        variant: 'error',
      });
      return;
    }

    if (isEditMode && !contractId) {
      enqueueSnackbar('Error: No se encontró el ID del contrato', {
        variant: 'error',
      });
      return;
    }

    setIsSaving(true);

    try {
      const programDates = programsData.map((program) => ({
        start: program.dateRange.startDate instanceof Date
          ? program.dateRange.startDate
          : new Date(program.dateRange.startDate),
        end: program.dateRange.endDate instanceof Date
          ? program.dateRange.endDate
          : new Date(program.dateRange.endDate),
      }));

      const minStartDate = new Date(Math.min(...programDates.map((d) => d.start.getTime())));
      const maxEndDate = new Date(Math.max(...programDates.map((d) => d.end.getTime())));

      if (isEditMode) {
        await putStudyPlan({
          contractId,
          studentId,
          startDate: minStartDate,
          endDate: maxEndDate,
          tuition: Number(matriculaValor),
          tuitionDiscount: Number(matriculaDescuento),
          tuitionAgreement: matriculaConvenio || null,
          feeDiscount: 0,
          feeAdditionalDiscount: 0,
          feePaymentTypeDiscount: 0,
          studentProgramInfo: programsData.map((program) => ({
            programId: program.programId || '',
            feeDiscount: program.discount,
            comment: program.comment || '',
          })),
        });
        enqueueSnackbar('Plan de estudios actualizado exitosamente', {
          variant: 'success',
        });
      } else {
        const contractData: PostContractData = {
          studentId,
          startDate: minStartDate,
          endDate: maxEndDate,
          tuition: Number(matriculaValor),
          tuitionDiscount: Number(matriculaDescuento),
          tuitionAgreement: matriculaConvenio || null,
          tuitionPaymentType: '',
          feeDiscountType: null,
          feeDiscount: 0,
          feeAdditionalDiscount: 0,
          feePaymentType: '',
          feePaymentTypeDiscount: 0,
          studentProgramInfo: programsData.map((program) => ({
            programId: program.programId || '',
            feeDiscount: program.discount,
            comment: program.comment || '',
          })),
          comment: matriculaComentario || undefined,
          installments: [],
          status: DRAFT,
          academicYear: studentInscriptionYear || new Date().getFullYear(),
        };

        await postContract(contractData);
        enqueueSnackbar('Plan de estudios creado exitosamente', {
          variant: 'success',
        });
      }
      history.push(`/tabla-registro/alumno/${studentId}?tab=plan-estudios&contractCreated=true`);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setIsSaving(false);
    }
  };

  const renderActionButtons = (programIndex: number) => (
    <Box>
      <IconButton
        size="small"
        color="error"
        onClick={() => handleEditProgram(programIndex)}
      >
        <Edit />
      </IconButton>
      <IconButton
        size="small"
        color="error"
        onClick={() => handleDeleteProgram(programIndex)}
      >
        <Delete />
      </IconButton>
    </Box>
  );

  useEffect(() => {
    const fetchStudentRelevantInfo = async () => {
      if (!studentId) {
        return;
      }

      try {
        const [enrollmentData, subjectsData] = await Promise.all([
          getEnrollmentSummaryByStudentId(studentId),
          getSubjects(),
        ]);

        const subjectsList = subjectsData.subjects || [];
        setSubjects(subjectsList.map((s: { id: string; name: string }) => ({
          id: s.id,
          name: s.name,
        })));

        const testAreas = enrollmentData.student?.testAreas || [];
        const testsComment = enrollmentData.student?.comment || '';
        const inscriptionYear = enrollmentData.student?.inscriptionYear;
        const locationName = enrollmentData.student?.location || '';
        const classYear = enrollmentData.student?.classYear || '';
        const schoolName = (() => {
          const school = enrollmentData.student?.school || '';
          const otherSchool = enrollmentData.student?.otherSchoolName;
          if (school.toLowerCase() === 'otro' && otherSchool) {
            return `Otro (${otherSchool})`;
          }
          return school;
        })();

        const testsToTake = testAreas.map((subjectName: string) => {
          const subject = subjectsList.find(
            (s: { id: string; name: string }) => s.name === subjectName,
          );
          return {
            id: subject ? Number(subject.id) : 0,
            name: subjectName,
          };
        }).filter((test: { id: number; name: string }) => test.id > 0);

        setStudentRelevantInfo({
          testsToTake,
          testsComment,
          locationName,
          inscriptionYear,
          classYear,
          schoolName,
        });
        setStudentInscriptionYear(
          typeof inscriptionYear === 'number' ? inscriptionYear : null,
        );
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
    };
    fetchStudentRelevantInfo();
  }, [studentId, enqueueSnackbar]);

  useEffect(() => {
    const fetchContractData = async () => {
      if (!isEditMode || !contractId) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await getContractById(contractId);
        const contract = Array.isArray(response.contract)
          ? response.contract[0]
          : response.contract;

        if (contract) {
          setMatriculaValor(String(contract.tuition || '0'));
          setMatriculaDescuento(String(contract.tuitionDiscount || '0'));
          setMatriculaConvenio(contract.tuitionAgreement || '');
          if (contract.contractDocuments && contract.contractDocuments.length > 0) {
            setMatriculaComentario(contract.contractDocuments[0].comment || '');
          }

          if (contract.studentPrograms && contract.studentPrograms.length > 0) {
            type StudentProgramResponse = {
              id: number;
              feeDiscount: number;
              comment: string | null;
              program: {
                id: number;
                name: string;
                fee: number;
                startDate: string | Date;
                endDate: string | Date;
                subject: {
                  id: number;
                  name: string;
                };
              };
            };

            const mappedPrograms: ProgramTableData[] = contract.studentPrograms.map(
              (sp: StudentProgramResponse) => {
                const program = sp.program || {};
                const subject = program.subject || {};
                const originalFee = program.fee || 0;
                const discount = sp.feeDiscount || 0;
                const finalFee = Math.max(0, originalFee - discount);
                const programYear = program.startDate
                  ? new Date(program.startDate).getFullYear()
                  : new Date().getFullYear();

                return {
                  id: String(sp.id),
                  subjectId: String(subject.id || ''),
                  subjectName: subject.name || '',
                  year: programYear,
                  programId: String(program.id || ''),
                  programName: program.name || '',
                  dateRange: {
                    startDate: program.startDate ? new Date(program.startDate) : new Date(),
                    endDate: program.endDate ? new Date(program.endDate) : new Date(),
                  },
                  originalFee,
                  discount,
                  comment: sp.comment || '',
                  finalFee,
                };
              },
            );
            setProgramsData(mappedPrograms);
          }
        }
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContractData();
  }, [isEditMode, contractId, enqueueSnackbar]);

  if (isLoading) {
    return (
      <Page title={isEditMode ? 'Editar plan de estudios' : 'Crear plan de estudios'}>
        <Box className={classes.root}>
          <Typography>Cargando plan de estudios...</Typography>
        </Box>
      </Page>
    );
  }

  return (
    <Page title={isEditMode ? 'Editar plan de estudios' : 'Crear plan de estudios'}>
      <Box className={classes.root}>
        <Box className={classes.backButtonContainer}>
          <IconButton
            onClick={() => (studentId ? history.push(`/tabla-registro/alumno/${studentId}?tab=plan-estudios`) : history.goBack())}
            className={classes.backButton}
            size="small"
          >
            <ArrowBackIos className={classes.backIcon} />
          </IconButton>
          <Typography className={classes.title}>
            {isEditMode ? 'Editar plan de estudios' : 'Crear plan de estudios'}
          </Typography>
        </Box>
        <Box className={classes.contentContainer}>
          <Box className={`${classes.relevantStudentInfoContainer} ${classes.borderBox}`}>
            <Box className={classes.relevantStudentInfo}>
              <Typography className={classes.relevantStudentInfoTitle}>
                Pruebas a rendir
              </Typography>
              {studentRelevantInfo?.testsToTake.map((test) => (
                <Box key={test.id} className={classes.studentRelevantInfoItem}>
                  <Typography className={classes.relevantStudentInfoText}>
                    {test.name}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box className={classes.relevantStudentInfo}>
              <Typography className={classes.relevantStudentInfoTitle}>
                Objetivos
              </Typography>
              <Typography className={classes.relevantStudentInfoText}>
                {studentRelevantInfo?.testsComment || 'No hay objetivos específicos'}
              </Typography>
            </Box>
            <Box className={classes.relevantStudentInfo}>
              <Typography className={classes.relevantStudentInfoTitle}>
                Sede y nivel
              </Typography>
              <Typography className={classes.relevantStudentInfoText}>
                {studentRelevantInfo?.locationName}
                {' '}
                - {studentRelevantInfo?.classYear}
                {' '}
                - {studentRelevantInfo?.inscriptionYear}
              </Typography>
            </Box>
            <Box className={classes.relevantStudentInfo}>
              <Typography className={classes.relevantStudentInfoTitle}>
                Colegio
              </Typography>
              <Typography className={classes.relevantStudentInfoText}>
                {studentRelevantInfo?.schoolName || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className={classes.programsContainer}>
          <Box className={classes.programsHeader}>
            <Typography className={classes.title}>Programas</Typography>
            <Button
              className={classes.addProgramButton}
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={() => {
                setEditingProgram(null);
                setModalOpen(true);
              }}
            >
              Agregar programa
            </Button>
          </Box>
          <CustomTable
            headers={STUDY_PLAN_TABLE_HEADERS}
            data={{
              count: programsData.length,
              values: programsData.map((program, index) => ({
                ...program,
                actions: renderActionButtons(index),
              })),
            }}
          />
        </Box>
        <Box className={classes.contentContainer}>
          <Box className={`${classes.borderBox} ${classes.matriculaContainer}`}>
            <Typography className={classes.title}>
              Matricula
            </Typography>
            <Box className={classes.matriculaInfoContainer}>
              <Box className={classes.matriculaInputs}>
                <TextField
                  fullWidth
                  label="Valor"
                  variant="outlined"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={matriculaValor}
                  onChange={({ target }) => {
                    const { value } = target;
                    setMatriculaValor(value);
                    if (Number(value) === 0) {
                      setMatriculaDescuento('0');
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment
                      position="start"
                      color="primary"
                    >
                      <Typography className={classes.adornmentTextCurrency}>
                        $
                      </Typography>
                    </InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Descuento"
                  variant="outlined"
                  type="number"
                  value={matriculaDescuento}
                  onChange={(e) => {
                    if (Number(e.target.value) > 100) {
                      enqueueSnackbar('El descuento no puede ser mayor a 100%', { variant: 'error' });
                      return;
                    }
                    setMatriculaDescuento(e.target.value);
                  }
                  }
                />
              </Box>
              <Box className={classes.matriculaInputs}>
                <FormControl fullWidth variant="outlined" className={classes.centeredLabelTextField}>
                  <InputLabel>Convenio</InputLabel>
                  <Select
                    value={matriculaConvenio}
                    onChange={(e) => setMatriculaConvenio(e.target.value)}
                    label="Convenio"
                  >
                    <MenuItem value="">
                      <em>Ninguno</em>
                    </MenuItem>
                    {agreementOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Comentario"
                  variant="outlined"
                  value={matriculaComentario}
                  onChange={(e) => setMatriculaComentario(e.target.value)}
                  className={classes.centeredLabelTextField}
                />
              </Box>
              <Box className={classes.matriculaResult}>
                <Typography className={classes.title}>
                  Valor Matrícula
                </Typography>
                {Number(matriculaDescuento) > 0 ? (
                  <>
                    <Typography className={classes.matriculaPriceOriginal}>
                      ${Number(matriculaValor).toLocaleString('es-CL')}
                    </Typography>
                    <Typography className={classes.matriculaPriceFinal}>
                      ${Math.max(0, Number(matriculaValor) * (1 - Number(matriculaDescuento) / 100)).toLocaleString('es-CL')}
                    </Typography>
                  </>
                ) : (
                  <Typography className={classes.matriculaPriceFinal}>
                    ${Number(matriculaValor).toLocaleString('es-CL')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={classes.contentContainer}>
          <Box style={{
            display: 'flex',
            justifyContent: 'center',
          }}
          >
            <Button
              className={classes.addProgramButton}
              variant="contained"
              color="primary"
              startIcon={<Check />}
              onClick={handleCreateStudyPlan}
              disabled={isSaving}
            >
              {(() => {
                if (isSaving) return 'Guardando...';
                if (isEditMode) return 'Actualizar';
                return 'Crear plan de estudios';
              })()}
            </Button>
          </Box>
        </Box>
      </Box>
      <AddProgramModal
        modalOpen={modalOpen}
        setModalOpen={(open) => {
          setModalOpen(open);
          if (!open) {
            setEditingProgram(null);
          }
        }}
        subjects={subjects}
        editingProgram={editingProgram ? {
          subjectId: editingProgram.subjectId || '',
          subjectName: editingProgram.subjectName,
          year: editingProgram.year,
          level: editingProgram.level || '',
          classYear: editingProgram.classYear || '',
          programId: editingProgram.programId || '',
          programName: editingProgram.programName,
          startDate: editingProgram.dateRange.startDate instanceof Date
            ? editingProgram.dateRange.startDate
            : new Date(editingProgram.dateRange.startDate),
          endDate: editingProgram.dateRange.endDate instanceof Date
            ? editingProgram.dateRange.endDate
            : new Date(editingProgram.dateRange.endDate),
          originalFee: String(editingProgram.originalFee),
          discount: String(editingProgram.discount),
          comment: editingProgram.comment || '',
        } : null}
        defaultYear={studentInscriptionYear}
        onAddProgram={(programData) => {
          const originalFeeNumber = Number(programData.originalFee);
          const discountNumber = Number(programData.discount);
          const finalFeeValue = Math.max(0, originalFeeNumber - discountNumber);

          if (editingProgram) {
            setProgramsData((prev) => prev.map((program) => {
              if (program.id === editingProgram.id) {
                return {
                  ...program,
                  subjectId: programData.subjectId,
                  subjectName: programData.subjectName || program.subjectName,
                  year: programData.year || new Date().getFullYear(),
                  programId: programData.programId,
                  programName: programData.programName || program.programName,
                  dateRange: {
                    startDate: programData.startDate,
                    endDate: programData.endDate,
                  },
                  originalFee: originalFeeNumber,
                  discount: discountNumber,
                  comment: programData.comment,
                  finalFee: finalFeeValue,
                };
              }
              return program;
            }));
            setEditingProgram(null);
          } else {
            const programExists = programsData.some(
              (program) => program.programId === programData.programId,
            );

            if (programExists) {
              enqueueSnackbar(
                `El programa "${programData.programName || programData.subjectName}" ya está agregado al plan de estudios`,
                {
                  variant: 'error',
                  autoHideDuration: 6000,
                },
              );
              return;
            }

            const newProgram: ProgramTableData = {
              id: `${Date.now()}`,
              subjectId: programData.subjectId,
              subjectName: programData.subjectName || '',
              year: programData.year || new Date().getFullYear(),
              level: programData.level || '',
              classYear: programData.classYear || '',
              programId: programData.programId,
              programName: programData.programName || '',
              dateRange: {
                startDate: programData.startDate,
                endDate: programData.endDate,
              },
              originalFee: originalFeeNumber,
              discount: discountNumber,
              comment: programData.comment,
              finalFee: finalFeeValue,
            };
            setProgramsData((prev) => [...prev, newProgram]);
          }
        }}
      />
    </Page>
  );
}

export default CreateStudyPlan;
